import { apiService } from './api.service'
import { 
  AIGenerationRequest, 
  AIGenerationResponse, 
  AIPromptTemplate,
  AIContentEnhancement,
  PaginatedResponse,
  PaginationRequest
} from '../types'

/** Request body for `POST /v1/ai/generate` (GenerateContentRequest). */
interface GenerateContentRequestDto {
  prompt: string
  templateType?: string
  options: {
    tone: string
    length: string
    creativity: number
    includeImages: boolean
    language: string
  }
}

/** Response body for `POST /v1/ai/generate` (GenerateContentResponse). */
interface GenerateContentResponseDto {
  id: string
  content: string
  tokensUsed: number
  model: string
  createdAt: string
}

const STYLE_TO_LENGTH: Record<string, string> = {
  concise: 'short',
  detailed: 'long',
}

/** Translate the add-in request into the API contract, folding selection context into the prompt. */
export const toGenerateRequest = (request: AIGenerationRequest): GenerateContentRequestDto => {
  const prompt = request.context
    ? `${request.prompt}\n\nContext from the document:\n${request.context}`
    : request.prompt
  return {
    prompt,
    templateType: request.templateId,
    options: {
      tone: request.options?.tone ?? 'professional',
      length: STYLE_TO_LENGTH[request.options?.style ?? ''] ?? 'medium',
      creativity: request.options?.temperature !== undefined
        ? Math.round(Math.min(1, Math.max(0, request.options.temperature)) * 100)
        : 50,
      includeImages: false,
      language: request.options?.language ?? 'en',
    },
  }
}

export class AIService {
  private readonly basePath = '/v1/ai'

  async generateContent(request: AIGenerationRequest): Promise<AIGenerationResponse> {
    const response = await apiService.post<GenerateContentResponseDto>(
      `${this.basePath}/generate`,
      toGenerateRequest(request)
    )
    const dto = response.data
    return {
      id: dto.id,
      content: dto.content,
      model: dto.model as AIGenerationResponse['model'],
      tokensUsed: dto.tokensUsed,
      cost: 0,
      duration: 0,
    }
  }

  async enhanceContent(
    content: string,
    enhancements: Array<'grammar' | 'style' | 'tone' | 'clarity' | 'seo'>
  ): Promise<AIContentEnhancement[]> {
    const response = await apiService.post<AIContentEnhancement[]>(
      `${this.basePath}/enhance`,
      { content, enhancements }
    )
    return response.data
  }

  async summarize(
    content: string,
    options?: {
      maxLength?: number
      style?: 'bullet' | 'paragraph'
    }
  ): Promise<{ summary: string }> {
    const response = await apiService.post<{ summary: string }>(
      `${this.basePath}/summarize`,
      { content, ...options }
    )
    return response.data
  }

  async translate(
    content: string,
    targetLanguage: string,
    sourceLanguage?: string
  ): Promise<{ translation: string; detectedLanguage?: string }> {
    const response = await apiService.post<{ translation: string; detectedLanguage?: string }>(
      `${this.basePath}/translate`,
      { content, targetLanguage, sourceLanguage }
    )
    return response.data
  }

  async extractKeywords(
    content: string,
    count: number = 10
  ): Promise<{ keywords: Array<{ keyword: string; score: number }> }> {
    const response = await apiService.post<{ keywords: Array<{ keyword: string; score: number }> }>(
      `${this.basePath}/extract-keywords`,
      { content, count }
    )
    return response.data
  }

  async checkGrammar(content: string): Promise<{
    issues: Array<{
      text: string
      suggestion: string
      reason: string
      position: { start: number; end: number }
    }>
  }> {
    const response = await apiService.post<{
      issues: Array<{
        text: string
        suggestion: string
        reason: string
        position: { start: number; end: number }
      }>
    }>(`${this.basePath}/grammar-check`, { content })
    return response.data
  }

  async generateFromTemplate(
    templateId: string,
    variables: Record<string, any>,
    options?: AIGenerationOptions
  ): Promise<AIGenerationResponse> {
    const response = await apiService.post<AIGenerationResponse>(
      `${this.basePath}/generate-from-template`,
      { templateId, variables, options }
    )
    return response.data
  }

  // Prompt template operations
  async getPromptTemplates(
    params?: PaginationRequest & {
      category?: string
      tags?: string[]
    }
  ): Promise<PaginatedResponse<AIPromptTemplate>> {
    return apiService.getPaginated<AIPromptTemplate>(`${this.basePath}/prompts`, params)
  }

  async getPromptTemplate(id: string): Promise<AIPromptTemplate> {
    const response = await apiService.get<AIPromptTemplate>(`${this.basePath}/prompts/${id}`)
    return response.data
  }

  async createPromptTemplate(
    template: Omit<AIPromptTemplate, 'id'>
  ): Promise<AIPromptTemplate> {
    const response = await apiService.post<AIPromptTemplate>(
      `${this.basePath}/prompts`,
      template
    )
    return response.data
  }

  async updatePromptTemplate(
    id: string,
    updates: Partial<AIPromptTemplate>
  ): Promise<AIPromptTemplate> {
    const response = await apiService.put<AIPromptTemplate>(
      `${this.basePath}/prompts/${id}`,
      updates
    )
    return response.data
  }

  async deletePromptTemplate(id: string): Promise<void> {
    await apiService.delete(`${this.basePath}/prompts/${id}`)
  }

  async testPromptTemplate(
    id: string,
    variables: Record<string, any>
  ): Promise<{ result: string }> {
    const response = await apiService.post<{ result: string }>(
      `${this.basePath}/prompts/${id}/test`,
      { variables }
    )
    return response.data
  }

  // Model information
  async getAvailableModels(): Promise<Array<{
    id: string
    name: string
    description: string
    capabilities: string[]
    costPerToken: number
  }>> {
    const response = await apiService.get<Array<{
      id: string
      name: string
      description: string
      capabilities: string[]
      costPerToken: number
    }>>(`${this.basePath}/models`)
    return response.data
  }

  async estimateCost(
    content: string,
    model: string
  ): Promise<{ tokens: number; estimatedCost: number }> {
    const response = await apiService.post<{ tokens: number; estimatedCost: number }>(
      `${this.basePath}/estimate-cost`,
      { content, model }
    )
    return response.data
  }

  // Excel-specific AI features
  async generateExcelFormula(
    description: string,
    context?: {
      columnNames?: string[]
      dataTypes?: Record<string, string>
    }
  ): Promise<{ formula: string; explanation: string }> {
    const response = await apiService.post<{ formula: string; explanation: string }>(
      `${this.basePath}/excel/generate-formula`,
      { description, context }
    )
    return response.data
  }

  async explainExcelFormula(formula: string): Promise<{
    explanation: string
    components: Array<{ part: string; description: string }>
  }> {
    const response = await apiService.post<{
      explanation: string
      components: Array<{ part: string; description: string }>
    }>(`${this.basePath}/excel/explain-formula`, { formula })
    return response.data
  }

  // PowerPoint-specific AI features
  async generateSlideContent(
    topic: string,
    slideType: 'title' | 'content' | 'comparison' | 'timeline' | 'conclusion',
    options?: {
      bulletPoints?: number
      tone?: string
    }
  ): Promise<{
    title: string
    content: string[]
    speakerNotes?: string
  }> {
    const response = await apiService.post<{
      title: string
      content: string[]
      speakerNotes?: string
    }>(`${this.basePath}/powerpoint/generate-slide`, {
      topic,
      slideType,
      ...options
    })
    return response.data
  }

  async generatePresentationOutline(
    topic: string,
    slideCount: number
  ): Promise<Array<{
    slideNumber: number
    title: string
    bulletPoints: string[]
    slideType: string
  }>> {
    const response = await apiService.post<Array<{
      slideNumber: number
      title: string
      bulletPoints: string[]
      slideType: string
    }>>(`${this.basePath}/powerpoint/generate-outline`, {
      topic,
      slideCount
    })
    return response.data
  }
}

export const aiService = new AIService()

interface AIGenerationOptions {
  temperature?: number
  maxTokens?: number
  tone?: string
  style?: string
  language?: string
  format?: string
  includeSources?: boolean
}